import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { 
  Card, Table, Empty, Tag, Button, Typography, Modal, Drawer,
  Select, Form, DatePicker, Space, Tooltip, Popconfirm,
  Row, Col, Input, Segmented, Alert, Switch, App, Spin
} from 'antd';
import { 
  PlusOutlined, EditOutlined, DeleteOutlined,
  ReloadOutlined, ClockCircleOutlined,
  CheckCircleOutlined, CloseCircleOutlined, SyncOutlined,
  StopOutlined, ExclamationCircleOutlined, FileTextOutlined,
  CopyOutlined, DownloadOutlined
} from '@ant-design/icons';
import { useEvent } from '../../hooks/useEvent';
import { useAuth } from '../../hooks/useAuth';
import { useTheme } from '../../contexts/useTheme';
import { JobsTimeline } from '../../components/JobsTimeline';
import '../../components/JobsTimeline/JobsTimeline.css';
import eventJobsService, { 
  type EventJob, 
  type JobType, 
  type JobStatus,
  type JobLogEntry,
  type CreateEventJobRequest,
  type UpdateEventJobRequest,
  JOB_TYPE_CONFIG,
  JOB_STATUS_CONFIG,
  VALID_JOB_TYPES,
  WEATHER_MESSAGE_TYPE_CONFIG
} from '../../api/eventJobsService';
import {
  generateWeatherMessageJobs,
  generateDailyForecastJobs,
  generateHourlyForecastJobs,
  generateMinutelyForecastJobs,
  generateBackupJobs,
  generatePipelineStatusCheckJobs,
  generateArchiveObservationsJobs,
  getJobCounts,
  type JobCounts
} from '../../utils/jobScheduleGenerator';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

const { Text } = Typography;
const { TextArea } = Input;

const jobTypeOptions: { value: JobType; label: string }[] = VALID_JOB_TYPES.map(type => ({
  value: type,
  label: `${JOB_TYPE_CONFIG[type].icon} ${JOB_TYPE_CONFIG[type].label}`,
}));

const getStatusIcon = (status: JobStatus) => {
  switch (status) {
    case 'pending':
      return <ClockCircleOutlined style={{ color: JOB_STATUS_CONFIG.pending.color }} />;
    case 'running':
      return <SyncOutlined spin style={{ color: JOB_STATUS_CONFIG.running.color }} />;
    case 'succeeded':
      return <CheckCircleOutlined style={{ color: JOB_STATUS_CONFIG.succeeded.color }} />;
    case 'failed':
      return <CloseCircleOutlined style={{ color: JOB_STATUS_CONFIG.failed.color }} />;
    default:
      return null;
  }
};

export const JobsPage = () => {
  const { message } = App.useApp();
  const { selectedEvent } = useEvent();
  const { authState } = useAuth();
  const { theme } = useTheme();
  
  // Data state
  const [jobs, setJobs] = useState<EventJob[]>([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false); // For refresh button indicator
  const [error, setError] = useState<string | null>(null);
  const [currentTime, setCurrentTime] = useState<Date>(new Date());
  
  // Filter state
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [jobTypeFilter, setJobTypeFilter] = useState<string>('all');
  const [timeFilter, setTimeFilter] = useState<string>('current_future'); // 'current_future' or 'all'
  
  // Selection state
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [deleting, setDeleting] = useState(false);
  
  // Modal states
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingJob, setEditingJob] = useState<EventJob | null>(null);
  const [submitting, setSubmitting] = useState(false);
  
  // Recreate jobs modal state
  const [isRecreateModalOpen, setIsRecreateModalOpen] = useState(false);
  const [enableWeatherMessages, setEnableWeatherMessages] = useState(true);
  const [enableDailyForecast, setEnableDailyForecast] = useState(true);
  const [enableHourlyForecast, setEnableHourlyForecast] = useState(true);
  const [enableMinutelyForecast, setEnableMinutelyForecast] = useState(true);
  const [enableBackup, setEnableBackup] = useState(true);
  const [enablePipelineStatusChecks, setEnablePipelineStatusChecks] = useState(true);
  const [enableArchiveObservations, setEnableArchiveObservations] = useState(true);
  const [pipelineMonitorStart, setPipelineMonitorStart] = useState<dayjs.Dayjs | null>(null);
  const [jobCounts, setJobCounts] = useState<JobCounts | null>(null);
  const [creatingJobs, setCreatingJobs] = useState(false);
  
  // Log drawer state
  const [logDrawerOpen, setLogDrawerOpen] = useState(false);
  const [logDrawerJob, setLogDrawerJob] = useState<EventJob | null>(null);
  const [logEntries, setLogEntries] = useState<JobLogEntry[]>([]);
  const [logLoading, setLogLoading] = useState(false);
  const [logError, setLogError] = useState<string | null>(null);

  const [form] = Form.useForm();

  // Table container ref for dynamic scroll height
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const [tableScrollY, setTableScrollY] = useState<number | undefined>(undefined);

  // Pagination state (controlled so page size selection persists across re-renders)
  const [pageSize, setPageSize] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);

  // Check if user has root role
  const hasAdminRole = authState.user?.role === 'root';
  
  // Get circuit timezone
  const circuitTimeZone = selectedEvent?.circuit?.time_zone;

  // Check if this is a demo event
  const isDemo = selectedEvent?.demo ?? false;
  
  // Check if this is a legacy event
  const isLegacyEvent = selectedEvent?.legacy === true;
  
  // Format the current time for display
  const currentTimeDisplay = useMemo(() => {
    const tz = circuitTimeZone || 'UTC';
    return dayjs(currentTime).tz(tz).format('MMM D, YYYY, HH:mm');
  }, [currentTime, circuitTimeZone]);

  // Fetch jobs for the selected event
  const fetchJobs = useCallback(async (showLoading: boolean = true, showRefreshing: boolean = false) => {
    if (!selectedEvent) return;
    
    // Only show loading overlay if explicitly requested (for initial loads)
    // For background refreshes, we update data silently
    if (showLoading) {
      setLoading(true);
    }
    if (showRefreshing) {
      setRefreshing(true);
    }
    setError(null);
    
    try {
      const statusParam = statusFilter === 'all' ? undefined : statusFilter;
      const jobTypeParam = jobTypeFilter === 'all' ? undefined : jobTypeFilter;
      
      const response = await eventJobsService.getEventJobs(
        selectedEvent.season_year,
        selectedEvent.circuit_id,
        selectedEvent.start_date,
        isDemo,
        statusParam,
        jobTypeParam
      );
      
      if (response.success && response.data) {
        setJobs(response.data);
      } else {
        setJobs([]);
        if (!response.success) {
          setError(response.errorData?.message || 'Failed to fetch jobs');
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch jobs');
      console.error('Failed to fetch jobs:', err);
    } finally {
      if (showLoading) {
        setLoading(false);
      }
      if (showRefreshing) {
        setRefreshing(false);
      }
    }
  }, [selectedEvent, isDemo, statusFilter, jobTypeFilter]);

  // Fetch jobs when event changes or filters change
  useEffect(() => {
    if (selectedEvent) {
      fetchJobs(true); // Show loading on initial load
    } else {
      setJobs([]);
    }
  }, [selectedEvent, fetchJobs]);

  // Reset to page 1 when filters change (avoid empty page when result set shrinks)
  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, jobTypeFilter, timeFilter]);

  // Measure table container and set table body scroll height (reserve for pagination)
  const TABLE_SCROLL_RESERVE_PX = 80;
  useEffect(() => {
    const el = tableContainerRef.current;
    if (!el) return;
    const updateScrollY = () => {
      if (!el.isConnected) return;
      const height = el.clientHeight;
      setTableScrollY(Math.max(200, height - TABLE_SCROLL_RESERVE_PX));
    };
    updateScrollY();
    const ro = new ResizeObserver(updateScrollY);
    ro.observe(el);
    return () => ro.disconnect();
  }, [loading, jobs.length]);

  // Update current time and refresh jobs every minute (aligned to clock minute)
  useEffect(() => {
    const now = new Date();
    const msUntilNextMinute = (60 - now.getSeconds()) * 1000 - now.getMilliseconds();
    
    let intervalId: ReturnType<typeof setInterval> | null = null;
    
    const alignTimeout = setTimeout(() => {
      // Fire immediately at the top of the minute
      setCurrentTime(new Date());
      if (selectedEvent) {
        fetchJobs(false, true); // Silent refresh - show button spinner but no table loading overlay
      }
      
      // Then repeat every minute
      intervalId = setInterval(() => {
        setCurrentTime(new Date());
        if (selectedEvent) {
          fetchJobs(false, true); // Silent refresh - show button spinner but no table loading overlay
        }
      }, 60 * 1000);
    }, msUntilNextMinute);
    
    return () => {
      clearTimeout(alignTimeout);
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [selectedEvent, fetchJobs]);

  // Handle create job
  const handleCreate = () => {
    form.resetFields();
    setEditingJob(null);
    
    // Default to next minute in circuit timezone
    // We create the time in circuit TZ, then convert to a "local" dayjs for the picker
    // The picker will display these values, and we'll interpret them as circuit TZ on submit
    const nowInCircuit = circuitTimeZone 
      ? dayjs().tz(circuitTimeZone) 
      : dayjs();
    const defaultInCircuit = nowInCircuit.add(1, 'minute').startOf('minute');
    // Create a dayjs without TZ info but with the same display values (for the DatePicker)
    const defaultScheduledAt = dayjs(defaultInCircuit.format('YYYY-MM-DD HH:mm:ss'));
    
    form.setFieldsValue({
      job_type: 'daily_forecast',
      scheduled_at: defaultScheduledAt,
    });
    
    setIsModalOpen(true);
  };

  // Handle edit job
  const handleEdit = (job: EventJob) => {
    setEditingJob(job);
    
    // Parse the scheduled_at to circuit timezone, then strip TZ info for the picker
    // The picker will display these values, and we'll interpret them as circuit TZ on submit
    const scheduledInCircuit = circuitTimeZone 
      ? dayjs(job.scheduled_at).tz(circuitTimeZone)
      : dayjs(job.scheduled_at);
    // Create a dayjs without TZ info but with the same display values (for the DatePicker)
    const scheduledAt = dayjs(scheduledInCircuit.format('YYYY-MM-DD HH:mm:ss'));
    
    form.setFieldsValue({
      job_type: job.job_type,
      scheduled_at: scheduledAt,
      session_code: job.job_config?.session_code,
      message_type: job.job_config?.message_type,
      job_config_json: job.job_config ? JSON.stringify(job.job_config, null, 2) : '',
    });
    
    setIsModalOpen(true);
  };

  // Handle form submit
  const handleSubmit = async (values: {
    job_type: JobType;
    scheduled_at: dayjs.Dayjs;
    session_code?: string;
    message_type?: string;
    job_config_json?: string;
  }) => {
    if (!selectedEvent) return;
    
    setSubmitting(true);
    
    try {
      // The DatePicker value is a "naive" dayjs (no TZ info)
      // We need to interpret it as circuit timezone and convert to UTC
      const pickedTimeStr = values.scheduled_at.format('YYYY-MM-DD HH:mm:ss');
      const scheduledInCircuitTz = circuitTimeZone
        ? dayjs.tz(pickedTimeStr, circuitTimeZone)
        : dayjs(pickedTimeStr);
      
      // Validate that scheduled_at is in the future (compare in same timezone context)
      const nowInCircuitTz = circuitTimeZone 
        ? dayjs().tz(circuitTimeZone) 
        : dayjs();
      if (scheduledInCircuitTz.isBefore(nowInCircuitTz)) {
        message.error('Scheduled time must be in the future');
        setSubmitting(false);
        return;
      }
      
      // Convert scheduled_at to UTC ISO string
      const scheduledAtUtc = scheduledInCircuitTz.utc().toISOString();
      
      // Build job_config based on job type
      let jobConfig: Record<string, unknown> | undefined;
      
      if (values.job_type === 'weather_message') {
        // For weather messages, we need session and message type
        if (!values.session_code || !values.message_type) {
          message.error('Session and message type are required for weather messages');
          setSubmitting(false);
          return;
        }
        
        // Verify session exists in event (session times are looked up at execution time)
        const session = selectedEvent.sessions?.find(s => s.session_name === values.session_code);
        if (!session) {
          message.error('Selected session not found in event');
          setSubmitting(false);
          return;
        }
        
        jobConfig = {
          session_code: values.session_code,
          message_type: values.message_type,
        };
      } else if (values.job_config_json && values.job_config_json.trim()) {
        // For other job types, parse JSON if provided
        try {
          jobConfig = JSON.parse(values.job_config_json);
        } catch {
          message.error('Invalid JSON in job config');
          setSubmitting(false);
          return;
        }
      }
      
      if (editingJob) {
        // Update existing job
        const updates: UpdateEventJobRequest = {
          scheduled_at: scheduledAtUtc,
          job_config: jobConfig,
        };
        
        const response = await eventJobsService.updateJob(editingJob.id, updates, isDemo);
        if (!response.success) {
          throw new Error(response.errorData?.message || 'Failed to update job');
        }
        message.success('Job updated successfully');
      } else {
        // Create new job
        const createRequest: CreateEventJobRequest = {
          job_type: values.job_type,
          scheduled_at: scheduledAtUtc,
          job_config: jobConfig,
        };
        
        const response = await eventJobsService.createJob(
          selectedEvent.season_year,
          selectedEvent.circuit_id,
          selectedEvent.start_date,
          createRequest,
          isDemo
        );
        if (!response.success) {
          throw new Error(response.errorData?.message || 'Failed to create job');
        }
        message.success('Job created successfully');
      }
      
      setIsModalOpen(false);
      fetchJobs(false); // Silent refresh after create/update
    } catch (err) {
      message.error(err instanceof Error ? err.message : 'Failed to save job');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle delete job
  const handleDelete = async (job: EventJob) => {
    try {
      const response = await eventJobsService.cancelJob(job.id, isDemo);
      if (!response.success) {
        throw new Error(response.errorData?.message || 'Failed to delete job');
      }
      message.success('Job deleted successfully');
      fetchJobs(false); // Silent refresh after delete
    } catch (err) {
      message.error(err instanceof Error ? err.message : 'Failed to delete job');
    }
  };

  // Handle delete selected jobs
  const handleDeleteSelected = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning('Please select jobs to delete');
      return;
    }

    try {
      setDeleting(true);
      
      const now = new Date();
      // Filter jobs to only include pending jobs scheduled in the future
      const jobsToDelete = jobs.filter(job => {
        if (!selectedRowKeys.includes(job.id)) return false;
        if (job.status !== 'pending') return false;
        const scheduledDate = new Date(job.scheduled_at);
        return scheduledDate > now;
      });

      if (jobsToDelete.length === 0) {
        message.warning('Selected jobs cannot be deleted. Only pending jobs scheduled in the future can be deleted.');
        setSelectedRowKeys([]);
        setDeleting(false);
        return;
      }

      // Delete all selected jobs in a single batch operation
      const idsToDelete = jobsToDelete.map(job => job.id);
      
      try {
        const result = await eventJobsService.deleteJobsBatch(idsToDelete, isDemo);
        
        if (result.success) {
          const deleted = result.data?.deleted || idsToDelete.length;
          message.success(`Deleted ${deleted} job${deleted > 1 ? 's' : ''} successfully`);
        } else {
          message.error('Failed to delete jobs');
        }
      } catch (error) {
        console.error('Failed to delete jobs:', error);
        message.error(error instanceof Error ? error.message : 'Failed to delete jobs');
      }

      setSelectedRowKeys([]);
      fetchJobs(false); // Silent refresh after delete
    } catch (err) {
      message.error(err instanceof Error ? err.message : 'Failed to delete jobs');
    } finally {
      setDeleting(false);
    }
  };

  // Handle delete all future jobs
  const handleDeleteAllFuture = async () => {
    if (!selectedEvent) return;

    try {
      setDeleting(true);
      
      const now = new Date();
      // Find all pending jobs scheduled in the future
      const futureJobs = jobs.filter(job => {
        if (job.status !== 'pending') return false;
        const scheduledDate = new Date(job.scheduled_at);
        return scheduledDate > now;
      });

      if (futureJobs.length === 0) {
        message.info('No future pending jobs to delete');
        setDeleting(false);
        return;
      }

      // Delete all future jobs in a single batch operation
      const idsToDelete = futureJobs.map(job => job.id);
      
      try {
        const result = await eventJobsService.deleteJobsBatch(idsToDelete, isDemo);
        
        if (result.success) {
          const deleted = result.data?.deleted || idsToDelete.length;
          message.success(`Deleted ${deleted} future job${deleted > 1 ? 's' : ''} successfully`);
        } else {
          message.error('Failed to delete jobs');
        }
      } catch (error) {
        console.error('Failed to delete jobs:', error);
        message.error(error instanceof Error ? error.message : 'Failed to delete jobs');
      }

      setSelectedRowKeys([]);
      fetchJobs(false); // Silent refresh after delete
    } catch (err) {
      message.error(err instanceof Error ? err.message : 'Failed to delete future jobs');
    } finally {
      setDeleting(false);
    }
  };

  // Handle stop job as failed (for stuck/hanging running jobs)
  const handleStopAsFailed = async (job: EventJob) => {
    try {
      const response = await eventJobsService.updateJob(job.id, { status: 'failed' }, isDemo);
      if (!response.success) {
        throw new Error(response.errorData?.message || 'Failed to stop job');
      }
      message.success('Job stopped and marked as failed');
      fetchJobs(false); // Silent refresh after stop
    } catch (err) {
      message.error(err instanceof Error ? err.message : 'Failed to stop job');
    }
  };

  // Handle view logs
  const fetchJobLogs = useCallback(async (job: EventJob) => {
    setLogDrawerJob(job);
    setLogDrawerOpen(true);
    setLogLoading(true);
    setLogError(null);
    setLogEntries([]);

    try {
      const response = await eventJobsService.getJobLogs(job.id, isDemo);
      if (response.success && response.data) {
        setLogEntries(response.data);
      } else {
        setLogError(response.errorData?.message || 'Failed to fetch logs');
      }
    } catch (err) {
      setLogError(err instanceof Error ? err.message : 'Failed to fetch logs');
    } finally {
      setLogLoading(false);
    }
  }, [isDemo]);

  // Calculate job counts when modal opens or toggles change
  useEffect(() => {
    if (!isRecreateModalOpen || !selectedEvent) {
      setJobCounts(null);
      return;
    }

    const sessions = selectedEvent.sessions || [];
    if (sessions.length === 0) {
      setJobCounts(null);
      return;
    }

    const circuitTimezone = selectedEvent.circuit?.time_zone || 'UTC';
    const startDate = selectedEvent.start_date;
    const endDate = selectedEvent.end_date;

    // Find first and last session
    const sortedSessions = [...sessions].sort((a, b) => 
      new Date(a.start_time).getTime() - new Date(b.start_time).getTime()
    );
    const firstSession = sortedSessions[0];
    const lastSession = sortedSessions[sortedSessions.length - 1];

    if (!firstSession || !lastSession) {
      setJobCounts(null);
      return;
    }

    // Generate job arrays
    const weatherMessageJobs = enableWeatherMessages 
      ? generateWeatherMessageJobs(sessions, circuitTimezone)
      : [];
    const dailyForecastJobs = enableDailyForecast
      ? generateDailyForecastJobs(startDate, firstSession.start_time, circuitTimezone, {
          includeHourlyPeriodDays: enableDailyForecast && enableHourlyForecast,
          lastSessionEnd: lastSession.end_time,
        })
      : [];
    const hourlyForecastJobs = enableHourlyForecast
      ? generateHourlyForecastJobs(firstSession.start_time, lastSession.end_time, circuitTimezone, {
          sessions,
        })
      : [];
    const minutelyForecastJobs = enableMinutelyForecast && !isLegacyEvent
      ? generateMinutelyForecastJobs(sessions, circuitTimezone)
      : [];
    const backupJobs = enableBackup
      ? generateBackupJobs(startDate, endDate, circuitTimezone)
      : [];
    const pipelineJobs = enablePipelineStatusChecks
      ? generatePipelineStatusCheckJobs(startDate, sessions, circuitTimezone, {
          monitorStartTime: pipelineMonitorStart?.utc().toISOString(),
        })
      : [];
    const archiveJobs = enableArchiveObservations
      ? generateArchiveObservationsJobs(endDate, circuitTimezone)
      : [];

    // Calculate counts
    const counts = getJobCounts(
      weatherMessageJobs,
      dailyForecastJobs,
      hourlyForecastJobs,
      minutelyForecastJobs,
      backupJobs,
      pipelineJobs,
      archiveJobs
    );

    setJobCounts(counts);
  }, [isRecreateModalOpen, selectedEvent, enableWeatherMessages, enableDailyForecast, enableHourlyForecast, enableMinutelyForecast, enableBackup, enablePipelineStatusChecks, enableArchiveObservations, pipelineMonitorStart, isLegacyEvent]);

  // Handle open recreate jobs modal
  const handleOpenRecreateModal = () => {
    if (!selectedEvent) {
      message.warning('No event selected');
      return;
    }

    if (!selectedEvent.sessions || selectedEvent.sessions.length === 0) {
      message.warning('Event has no sessions. Cannot generate jobs.');
      return;
    }

    // Reset toggles to default
    setEnableWeatherMessages(true);
    setEnableDailyForecast(true);
    setEnableHourlyForecast(true);
    setEnableMinutelyForecast(true);
    setEnableBackup(true);
    setEnablePipelineStatusChecks(true);
    setPipelineMonitorStart(null);
    setIsRecreateModalOpen(true);
  };

  // Handle create jobs
  const handleCreateJobs = async () => {
    if (!selectedEvent || !selectedEvent.sessions || selectedEvent.sessions.length === 0) {
      message.error('Event or sessions not available');
      return;
    }

    try {
      setCreatingJobs(true);

      const circuitTimezone = selectedEvent.circuit?.time_zone || 'UTC';
      const startDate = selectedEvent.start_date;
      const endDate = selectedEvent.end_date;
      const sessions = selectedEvent.sessions;

      // Find first and last session
      const sortedSessions = [...sessions].sort((a, b) => 
        new Date(a.start_time).getTime() - new Date(b.start_time).getTime()
      );
      const firstSession = sortedSessions[0];
      const lastSession = sortedSessions[sortedSessions.length - 1];

      if (!firstSession || !lastSession) {
        message.error('Invalid session data');
        return;
      }

      // Generate all job schedules
      const allJobs: Array<{ job: CreateEventJobRequest; type: string }> = [];

      if (enableWeatherMessages) {
        const jobs = generateWeatherMessageJobs(sessions, circuitTimezone);
        allJobs.push(...jobs.map(job => ({ job, type: 'weather_message' })));
      }

      if (enableDailyForecast) {
        const jobs = generateDailyForecastJobs(startDate, firstSession.start_time, circuitTimezone, {
          includeHourlyPeriodDays: enableDailyForecast && enableHourlyForecast,
          lastSessionEnd: lastSession.end_time,
        });
        allJobs.push(...jobs.map(job => ({ job, type: 'daily_forecast' })));
      }

      if (enableHourlyForecast) {
        const jobs = generateHourlyForecastJobs(firstSession.start_time, lastSession.end_time, circuitTimezone, {
          sessions,
        });
        allJobs.push(...jobs.map(job => ({ job, type: 'hourly_forecast' })));
      }

      if (enableMinutelyForecast && !selectedEvent.legacy) {
        const jobs = generateMinutelyForecastJobs(sessions, circuitTimezone);
        allJobs.push(...jobs.map(job => ({ job, type: 'minutely_forecast' })));
      }

      if (enableBackup) {
        const jobs = generateBackupJobs(startDate, endDate, circuitTimezone);
        allJobs.push(...jobs.map(job => ({ job, type: 'backup' })));
      }

      if (enablePipelineStatusChecks) {
        const jobs = generatePipelineStatusCheckJobs(startDate, sessions, circuitTimezone, {
          monitorStartTime: pipelineMonitorStart?.utc().toISOString(),
        });
        allJobs.push(...jobs.map(job => ({ job, type: job.job_type })));
      }

      if (enableArchiveObservations) {
        const jobs = generateArchiveObservationsJobs(endDate, circuitTimezone);
        allJobs.push(...jobs.map(job => ({ job, type: 'archive_observations' })));
      }

      // Filter out jobs scheduled in the past
      const now = new Date();
      const validJobs = allJobs.filter(({ job }) => {
        const scheduledDate = new Date(job.scheduled_at);
        return scheduledDate > now;
      });

      const skippedCount = allJobs.length - validJobs.length;
      if (skippedCount > 0) {
        message.warning(`${skippedCount} job${skippedCount > 1 ? 's' : ''} scheduled in the past will be skipped`);
      }

      if (validJobs.length === 0) {
        message.warning('No valid jobs to create (all are scheduled in the past)');
        setCreatingJobs(false);
        return;
      }

      // Show initial progress
      message.loading({ content: `Creating ${validJobs.length} job${validJobs.length > 1 ? 's' : ''} in batch...`, key: 'creating-jobs', duration: 0 });
      
      // Extract just the job objects for batch creation
      const jobsToCreate = validJobs.map(({ job }) => job);
      
      try {
        // Create all jobs in a single batch operation
        const result = await eventJobsService.createJobsBatch(
          selectedEvent.season_year,
          selectedEvent.circuit_id,
          selectedEvent.start_date,
          jobsToCreate,
          isDemo
        );
        
        // Close loading message
        message.destroy('creating-jobs');
        
        if (result.success && result.data) {
          const created = result.data.created || (Array.isArray(result.data) ? result.data.length : 0);
          const skipped = result.data.skipped || 0;
          
          if (created > 0) {
            message.success(`Created ${created} job${created > 1 ? 's' : ''} successfully`);
          }
          if (skippedCount > 0 || skipped > 0) {
            const totalSkipped = skippedCount + skipped;
            message.info(`Skipped ${totalSkipped} job${totalSkipped > 1 ? 's' : ''} scheduled in the past or invalid`);
          }
          if (result.data.invalidJobs && result.data.invalidJobs.length > 0) {
            message.warning(`${result.data.invalidJobs.length} job${result.data.invalidJobs.length > 1 ? 's' : ''} failed validation`);
          }
        } else {
          const serverMessage = result.errorData?.message;
          message.error(serverMessage || 'Failed to create jobs');
        }
      } catch (error) {
        message.destroy('creating-jobs');
        console.error('Failed to create jobs:', error);
        message.error(error instanceof Error ? error.message : 'Failed to create jobs');
      }

      setIsRecreateModalOpen(false);
      fetchJobs(false); // Silent refresh after creating jobs
    } catch (err) {
      message.error(err instanceof Error ? err.message : 'Failed to create jobs');
    } finally {
      setCreatingJobs(false);
    }
  };

  // Format timestamp for display
  const formatTime = (timestamp: string | null) => {
    return eventJobsService.formatTimestamp(timestamp, circuitTimeZone);
  };

  // Watch job_type to show/hide weather message fields
  const watchedJobType = Form.useWatch('job_type', form);

  // Get session options from event
  const sessionOptions = selectedEvent?.sessions?.map(s => ({
    value: s.session_name,
    label: s.session_name,
  })) || [];

  // Row selection configuration
  const rowSelection = hasAdminRole ? {
    selectedRowKeys,
    onChange: (selectedKeys: React.Key[]) => {
      setSelectedRowKeys(selectedKeys);
    },
    getCheckboxProps: (job: EventJob) => {
      const now = new Date();
      const scheduledDate = new Date(job.scheduled_at);
      const canDelete = job.status === 'pending' && scheduledDate > now;
      return {
        disabled: !canDelete,
        name: job.id,
      };
    },
  } : undefined;

  // Count future pending jobs
  const futureJobsCount = useMemo(() => {
    const now = new Date();
    return jobs.filter(job => {
      if (job.status !== 'pending') return false;
      const scheduledDate = new Date(job.scheduled_at);
      return scheduledDate > now;
    }).length;
  }, [jobs]);

  // Table columns
  const columns: ColumnsType<EventJob> = [
    {
      title: 'Job ID',
      dataIndex: 'id',
      key: 'id',
      width: 300,
      render: (id: string) => (
        <Text code style={{ fontSize: '12px' }}>{id}</Text>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: JobStatus) => (
        <Tag 
          icon={getStatusIcon(status)} 
          color={JOB_STATUS_CONFIG[status]?.color}
        >
          {JOB_STATUS_CONFIG[status]?.label || status}
        </Tag>
      ),
    },
    {
      title: 'Job Type',
      dataIndex: 'job_type',
      key: 'job_type',
      width: 160,
      render: (jobType: JobType) => (
        <Space>
          <span>{JOB_TYPE_CONFIG[jobType]?.icon}</span>
          <span>{JOB_TYPE_CONFIG[jobType]?.label || jobType}</span>
        </Space>
      ),
    },
    {
      title: 'Scheduled At',
      dataIndex: 'scheduled_at',
      key: 'scheduled_at',
      width: 180,
      render: (timestamp: string) => formatTime(timestamp),
      sorter: (a, b) => new Date(a.scheduled_at).getTime() - new Date(b.scheduled_at).getTime(),
      defaultSortOrder: 'ascend',
    },
    {
      title: 'Details',
      key: 'details',
      render: (_, job) => {
        const desc = eventJobsService.getJobConfigDescription(job.job_type, job.job_config);
        return desc ? <Text type="secondary">{desc}</Text> : <Text type="secondary">-</Text>;
      },
    },
    {
      title: 'Started',
      dataIndex: 'started_at',
      key: 'started_at',
      width: 160,
      render: (timestamp: string | null) => formatTime(timestamp),
    },
    {
      title: 'Completed',
      dataIndex: 'completed_at',
      key: 'completed_at',
      width: 160,
      render: (timestamp: string | null) => formatTime(timestamp),
    },
    {
      title: 'Error',
      dataIndex: 'error_message',
      key: 'error_message',
      width: 200,
      ellipsis: true,
      render: (error: string | null) => error ? (
        <Tooltip title={error}>
          <Text type="danger" ellipsis style={{ maxWidth: 180 }}>
            <ExclamationCircleOutlined /> {error}
          </Text>
        </Tooltip>
      ) : '-',
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 160,
      fixed: 'right',
      render: (_, job) => {
        // Check if job is scheduled in the future
        const isScheduledInFuture = dayjs(job.scheduled_at).isAfter(dayjs());
        
        const canDelete = hasAdminRole && job.status === 'pending' && isScheduledInFuture;
        // Only pending jobs scheduled in the future can be edited
        const canEdit = hasAdminRole && job.status === 'pending' && isScheduledInFuture;
        // Can stop running jobs as failed (for stuck/hanging jobs)
        const canStopAsFailed = hasAdminRole && job.status === 'running';
        // Logs available for root users on any job that has started
        const canViewLogs = hasAdminRole && job.status !== 'pending';
        
        return (
          <Space size="small">
            {canViewLogs && (
              <Tooltip title="View Logs">
                <Button 
                  type="link" 
                  size="small" 
                  icon={<FileTextOutlined />} 
                  onClick={() => fetchJobLogs(job)}
                />
              </Tooltip>
            )}
            {canStopAsFailed && (
              <Tooltip title="Stop as Failed">
                <Popconfirm
                  title="Stop this job as failed?"
                  description="This will mark the job as failed. Use this for stuck or hanging jobs."
                  onConfirm={() => handleStopAsFailed(job)}
                  okText="Stop as Failed"
                  cancelText="No"
                  okButtonProps={{ danger: true }}
                >
                  <Button type="link" size="small" danger icon={<StopOutlined />} />
                </Popconfirm>
              </Tooltip>
            )}
            {canEdit && (
              <Tooltip title="Edit">
                <Button 
                  type="link" 
                  size="small" 
                  icon={<EditOutlined />} 
                  onClick={() => handleEdit(job)}
                />
              </Tooltip>
            )}
            {canDelete && (
              <Tooltip title="Delete">
                <Popconfirm
                  title="Delete this job?"
                  description="This will permanently delete the job from the database. This action cannot be undone."
                  onConfirm={() => handleDelete(job)}
                  okText="Yes"
                  cancelText="No"
                >
                  <Button type="link" size="small" danger icon={<DeleteOutlined />} />
                </Popconfirm>
              </Tooltip>
            )}
          </Space>
        );
      },
    },
  ];

  // Filter jobs by time (current/future vs all)
  const filteredJobs = useMemo(() => {
    if (timeFilter === 'all') {
      return jobs;
    }
    // Filter to show only current/future jobs (scheduled_at >= now)
    const now = new Date();
    return jobs.filter(job => {
      const scheduledDate = new Date(job.scheduled_at);
      return scheduledDate >= now;
    });
  }, [jobs, timeFilter]);

  // Status filter options with counts (using filtered jobs)
  const statusCounts = {
    all: filteredJobs.length,
    pending: filteredJobs.filter(j => j.status === 'pending').length,
    running: filteredJobs.filter(j => j.status === 'running').length,
    succeeded: filteredJobs.filter(j => j.status === 'succeeded').length,
    failed: filteredJobs.filter(j => j.status === 'failed').length,
  };

  if (!selectedEvent) {
    return (
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', padding: '8px' }}>
        <Card style={{ flex: 1 }}>
          <Empty description="Please select an event to manage jobs" />
        </Card>
      </div>
    );
  }

  // Show message for legacy events
  if (isLegacyEvent) {
    return (
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', padding: '8px' }}>
        <Card style={{ flex: 1 }}>
          <Empty 
            description={
              <div style={{ textAlign: 'center' }}>
                <div style={{ marginBottom: 8, fontSize: 16, fontWeight: 500 }}>
                  Jobs are not available for legacy events
                </div>
                <div style={{ color: 'rgba(0, 0, 0, 0.45)', fontSize: 14 }}>
                  Legacy events are read-only and do not support job management.
                </div>
              </div>
            }
          />
        </Card>
      </div>
    );
  }

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', padding: 0, gap: 0 }}>
      {/* Timeline Card */}
      <Card
        className="jobs-timeline-card"
        styles={{ body: { padding: '8px 12px' } }}
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span>Jobs Timeline</span>
              <span style={{ 
                fontSize: '12px', 
                fontWeight: 'normal',
                color: theme === 'dark' ? 'rgba(255, 255, 255, 0.85)' : 'rgba(0, 0, 0, 0.65)',
                backgroundColor: theme === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
                padding: '2px 10px',
                borderRadius: '4px',
              }}>
                {currentTimeDisplay}
                {circuitTimeZone && (
                  <span style={{ 
                    color: theme === 'dark' ? 'rgba(255, 255, 255, 0.45)' : 'rgba(0, 0, 0, 0.45)',
                    marginLeft: 6,
                  }}>
                    ({circuitTimeZone})
                  </span>
                )}
              </span>
            </div>
          </div>
        }
        extra={
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            {/* Status Legend */}
            <div style={{ display: 'flex', gap: '12px', fontSize: '11px', alignItems: 'center' }}>
              {Object.entries(JOB_STATUS_CONFIG).map(([status, config]) => (
                <span key={status} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <span 
                    className="legend-diamond"
                    style={{ 
                      backgroundColor: config.color,
                      width: '8px',
                      height: '8px',
                      transform: 'rotate(45deg)',
                      display: 'inline-block',
                    }}
                  />
                  {config.label}
                </span>
              ))}
            </div>
            {/* Action Buttons */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <Tooltip title="Refresh">
                <Button 
                  icon={<ReloadOutlined spin={refreshing} />} 
                  onClick={() => fetchJobs(false, true)}
                  size="small"
                  type="text"
                />
              </Tooltip>
              {hasAdminRole && (
                <>
                  {selectedRowKeys.length > 0 && (
                    <Popconfirm
                      title={`Delete ${selectedRowKeys.length} selected job${selectedRowKeys.length > 1 ? 's' : ''}?`}
                      description="This will permanently delete the selected jobs from the database. This action cannot be undone."
                      onConfirm={handleDeleteSelected}
                      okText="Delete"
                      cancelText="Cancel"
                      okButtonProps={{ danger: true }}
                    >
                      <Button 
                        danger
                        icon={<DeleteOutlined />} 
                        loading={deleting}
                        size="small"
                      >
                        Delete Selected ({selectedRowKeys.length})
                      </Button>
                    </Popconfirm>
                  )}
                  {futureJobsCount > 0 && (
                    <Popconfirm
                      title={`Delete all ${futureJobsCount} future pending job${futureJobsCount > 1 ? 's' : ''}?`}
                      description="This will permanently delete all future pending jobs from the database. This action cannot be undone."
                      onConfirm={handleDeleteAllFuture}
                      okText="Delete All"
                      cancelText="Cancel"
                      okButtonProps={{ danger: true }}
                    >
                      <Button 
                        danger
                        icon={<DeleteOutlined />} 
                        loading={deleting}
                        size="small"
                      >
                        Delete All Future ({futureJobsCount})
                      </Button>
                    </Popconfirm>
                  )}
                  <Tooltip title="Create Job">
                    <Button 
                      type="primary" 
                      icon={<PlusOutlined />} 
                      onClick={handleCreate}
                      size="small"
                    />
                  </Tooltip>
                  <Tooltip title="Recreate Future Jobs">
                    <Button 
                      type="default" 
                      icon={<ReloadOutlined />} 
                      onClick={handleOpenRecreateModal}
                      size="small"
                    >
                      Recreate Jobs
                    </Button>
                  </Tooltip>
                </>
              )}
            </div>
          </div>
        }
      >
        {selectedEvent?.start_date && selectedEvent?.end_date && (
          <JobsTimeline
            jobs={jobs}
            sessions={selectedEvent.sessions}
            eventStartDate={selectedEvent.start_date}
            eventEndDate={selectedEvent.end_date}
            circuitTimeZone={circuitTimeZone}
            loading={loading}
            onJobClick={(job) => {
              if (job.status === 'pending') {
                handleEdit(job);
              }
            }}
          />
        )}
      </Card>

      {/* Table Card */}
      <Card style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', borderRadius: 0 }} styles={{ body: { flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', padding: '8px 12px' } }}>
        {/* Filters */}
        <Row gutter={16} align="middle" style={{ marginBottom: 12, flexShrink: 0 }}>
          <Col>
            <Space size="small">
              <Text type="secondary">Time:</Text>
              <Segmented
                size="small"
                value={timeFilter}
                onChange={(value) => setTimeFilter(value as string)}
                options={[
                  { 
                    value: 'current_future', 
                    label: 'Current/Future'
                  },
                  { 
                    value: 'all', 
                    label: 'All'
                  },
                ]}
              />
            </Space>
          </Col>
          <Col>
            <Space size="small">
              <Text type="secondary">Status:</Text>
              <Segmented
                size="small"
                value={statusFilter}
                onChange={(value) => setStatusFilter(value as string)}
                options={[
                  { 
                    value: 'all', 
                    label: (
                      <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        All
                        <Tag style={{ margin: 0, fontSize: 11, padding: '0 4px', lineHeight: '16px' }}>{statusCounts.all}</Tag>
                      </span>
                    )
                  },
                  { 
                    value: 'pending', 
                    label: (
                      <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <ClockCircleOutlined style={{ color: JOB_STATUS_CONFIG.pending.color }} />
                        Pending
                        <Tag color="default" style={{ margin: 0, fontSize: 11, padding: '0 4px', lineHeight: '16px' }}>{statusCounts.pending}</Tag>
                      </span>
                    )
                  },
                  { 
                    value: 'running', 
                    label: (
                      <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <SyncOutlined spin style={{ color: JOB_STATUS_CONFIG.running.color }} />
                        Running
                        <Tag color="processing" style={{ margin: 0, fontSize: 11, padding: '0 4px', lineHeight: '16px' }}>{statusCounts.running}</Tag>
                      </span>
                    )
                  },
                  { 
                    value: 'succeeded', 
                    label: (
                      <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <CheckCircleOutlined style={{ color: JOB_STATUS_CONFIG.succeeded.color }} />
                        Done
                        <Tag color="success" style={{ margin: 0, fontSize: 11, padding: '0 4px', lineHeight: '16px' }}>{statusCounts.succeeded}</Tag>
                      </span>
                    )
                  },
                  { 
                    value: 'failed', 
                    label: (
                      <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <CloseCircleOutlined style={{ color: JOB_STATUS_CONFIG.failed.color }} />
                        Failed
                        <Tag color="error" style={{ margin: 0, fontSize: 11, padding: '0 4px', lineHeight: '16px' }}>{statusCounts.failed}</Tag>
                      </span>
                    )
                  },
                ]}
              />
            </Space>
          </Col>
          <Col>
            <Space size="small">
              <Text type="secondary">Type:</Text>
              <Select
                value={jobTypeFilter}
                onChange={setJobTypeFilter}
                size="small"
                style={{ width: 160 }}
                options={[
                  { value: 'all', label: 'All Types' },
                  ...VALID_JOB_TYPES.map(type => ({
                    value: type,
                    label: `${JOB_TYPE_CONFIG[type].icon} ${JOB_TYPE_CONFIG[type].label}`,
                  })),
                ]}
              />
            </Space>
          </Col>
        </Row>

        {error && (
          <div style={{ marginBottom: 12, color: '#f5222d', flexShrink: 0 }}>
            Error: {error}
          </div>
        )}

        <div ref={tableContainerRef} style={{ flex: 1, minHeight: 0 }}>
          <Table
            dataSource={filteredJobs}
            columns={columns}
            rowKey="id"
            rowSelection={rowSelection}
            loading={loading}
            size="small"
            scroll={{ x: 1000, ...(tableScrollY != null ? { y: tableScrollY } : {}) }}
            pagination={{
              current: currentPage,
              pageSize,
              showSizeChanger: true,
              showTotal: (total) => `${total} jobs${selectedRowKeys.length > 0 ? ` (${selectedRowKeys.length} selected)` : ''}`,
              size: 'small',
              onChange: (page, size) => {
                setCurrentPage(page);
                setPageSize(size);
              },
            }}
            locale={{
              emptyText: <Empty description="No jobs found" />,
            }}
          />
        </div>
      </Card>

      {/* Create/Edit Modal */}
      <Modal
        title={editingJob ? 'Edit Job' : 'Create Job'}
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        footer={null}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="job_type"
            label="Job Type"
            rules={[{ required: true, message: 'Please select a job type' }]}
          >
            <Select
              options={jobTypeOptions}
              disabled={!!editingJob} // Can't change job type after creation
              placeholder="Select job type"
            />
          </Form.Item>

          <Form.Item
            name="scheduled_at"
            label={`Scheduled At (${circuitTimeZone || 'Local Time'})`}
            rules={[{ required: true, message: 'Please select scheduled time' }]}
          >
            <DatePicker
              showTime
              format="YYYY-MM-DD HH:mm:ss"
              style={{ width: '100%' }}
            />
          </Form.Item>

          {/* Weather Message specific fields */}
          {watchedJobType === 'weather_message' && (
            <>
              <Form.Item
                name="session_code"
                label="Session"
                rules={[{ required: true, message: 'Please select a session' }]}
              >
                <Select
                  options={sessionOptions}
                  placeholder="Select session"
                />
              </Form.Item>

              <Form.Item
                name="message_type"
                label="Message Type"
                rules={[{ required: true, message: 'Please select message type' }]}
              >
                <Select
                  options={Object.entries(WEATHER_MESSAGE_TYPE_CONFIG).map(([key, config]) => ({
                    value: key,
                    label: `${config.icon} ${config.label}`,
                  }))}
                  placeholder="Select message type"
                />
              </Form.Item>
            </>
          )}

          {/* Advanced config for other job types */}
          {watchedJobType && watchedJobType !== 'weather_message' && (
            <Form.Item
              name="job_config_json"
              label="Job Config (JSON, optional)"
              extra="Advanced: provide custom configuration as JSON"
            >
              <TextArea
                rows={4}
                placeholder='{"dry_run": true}'
              />
            </Form.Item>
          )}

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setIsModalOpen(false)}>
                Cancel
              </Button>
              <Button type="primary" htmlType="submit" loading={submitting}>
                {editingJob ? 'Update' : 'Create'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Recreate Jobs Modal */}
      <Modal
        title="Recreate Future Jobs"
        open={isRecreateModalOpen}
        onCancel={() => setIsRecreateModalOpen(false)}
        footer={null}
        width={1200}
        destroyOnHidden
      >
        <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          <Alert
            title="Job Recreation"
            description="Generate future jobs for this event based on the current event configuration. All jobs will be scheduled in the circuit's timezone and stored in UTC. Only jobs scheduled in the future will be created."
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />

          {!selectedEvent?.sessions || selectedEvent.sessions.length === 0 ? (
            <Alert
              title="No Sessions Available"
              description="This event has no sessions configured. Cannot generate jobs without sessions."
              type="warning"
              showIcon
            />
          ) : (
            <Space orientation="vertical" size="small" style={{ width: '100%' }}>
              {/* Row 1: Weather Messages and Daily Forecast */}
              <div style={{ display: 'flex', gap: 12 }}>
                <Card size="small" style={{ flex: 1, padding: '12px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 500, marginBottom: 2, fontSize: '14px' }}>Weather Messages</div>
                      <div style={{ fontSize: '11px', color: '#666', lineHeight: '1.4' }}>
                        Temperature and rain probability alerts before sessions. All sessions get the same schedule: temperature at 3 hours before, temperature at 1 hour before, rain probability at 1 hour before, and temperature at 15 minutes before.
                      </div>
                    </div>
                    <Switch
                      checked={enableWeatherMessages}
                      onChange={setEnableWeatherMessages}
                      style={{ marginLeft: 12, flexShrink: 0 }}
                    />
                  </div>
                  {jobCounts && enableWeatherMessages && (
                    <div style={{ fontSize: '11px', color: '#1890ff', marginTop: 4 }}>
                      {jobCounts.weatherMessages} jobs will be created
                    </div>
                  )}
                </Card>

                <Card size="small" style={{ flex: 1, padding: '12px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 500, marginBottom: 2, fontSize: '14px' }}>Minutely Forecast Jobs</div>
                      <div style={{ fontSize: '11px', color: '#666', lineHeight: '1.4' }}>
                        30-minute minutely forecasts from 30 minutes before each session until session end. Jobs run every 5 minutes. Not available for legacy events.
                      </div>
                    </div>
                    <Switch
                      checked={enableMinutelyForecast && !isLegacyEvent}
                      disabled={isLegacyEvent}
                      onChange={setEnableMinutelyForecast}
                      style={{ marginLeft: 12, flexShrink: 0 }}
                    />
                  </div>
                  {jobCounts && enableMinutelyForecast && !isLegacyEvent && (
                    <div style={{ fontSize: '11px', color: '#1890ff', marginTop: 4 }}>
                      {jobCounts.minutelyForecast} jobs will be created
                    </div>
                  )}
                </Card>

                <Card size="small" style={{ flex: 1, padding: '12px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 500, marginBottom: 2, fontSize: '14px' }}>Daily Forecast Jobs</div>
                      <div style={{ fontSize: '11px', color: '#666', lineHeight: '1.4' }}>
                        Daily forecast generation from event start until day before first session. Jobs run every 4 hours (00:00, 04:00, 08:00, 12:00, 16:00, 20:00) in circuit timezone.
                      </div>
                    </div>
                    <Switch
                      checked={enableDailyForecast}
                      onChange={setEnableDailyForecast}
                      style={{ marginLeft: 12, flexShrink: 0 }}
                    />
                  </div>
                  {jobCounts && enableDailyForecast && (
                    <div style={{ fontSize: '11px', color: '#1890ff', marginTop: 4 }}>
                      {jobCounts.dailyForecast} jobs will be created
                    </div>
                  )}
                </Card>
              </div>

              {/* Row 2: Hourly Forecast and Backup */}
              <div style={{ display: 'flex', gap: 12 }}>
                <Card size="small" style={{ flex: 1, padding: '12px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 500, marginBottom: 2, fontSize: '14px' }}>Hourly Forecast Jobs</div>
                      <div style={{ fontSize: '11px', color: '#666', lineHeight: '1.4' }}>
                        Hourly forecast from day before first session until end of race. Jobs run every 4 hours in circuit timezone, plus 1 hour before each session start.
                      </div>
                    </div>
                    <Switch
                      checked={enableHourlyForecast}
                      onChange={setEnableHourlyForecast}
                      style={{ marginLeft: 12, flexShrink: 0 }}
                    />
                  </div>
                  {jobCounts && enableHourlyForecast && (
                    <div style={{ fontSize: '11px', color: '#1890ff', marginTop: 4 }}>
                      {jobCounts.hourlyForecast} jobs will be created
                    </div>
                  )}
                </Card>

                <Card size="small" style={{ flex: 1, padding: '12px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 500, marginBottom: 2, fontSize: '14px' }}>Backup Jobs</div>
                      <div style={{ fontSize: '11px', color: '#666', lineHeight: '1.4' }}>
                        Daily BigQuery table backups. One job per day at end of day (23:59:59) in circuit timezone, for all days of the event.
                      </div>
                    </div>
                    <Switch
                      checked={enableBackup}
                      onChange={setEnableBackup}
                      style={{ marginLeft: 12, flexShrink: 0 }}
                    />
                  </div>
                  {jobCounts && enableBackup && (
                    <div style={{ fontSize: '11px', color: '#1890ff', marginTop: 4 }}>
                      {jobCounts.backup} jobs will be created
                    </div>
                  )}
                </Card>

                <Card size="small" style={{ flex: 1, padding: '12px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 500, marginBottom: 2, fontSize: '14px' }}>Pipeline Status Checks</div>
                      <div style={{ fontSize: '11px', color: '#666', lineHeight: '1.4' }}>
                        Stations and radar pipeline monitoring. Every 15 min during event, every 5 min during sessions. No jobs after last session.
                      </div>
                    </div>
                    <Switch
                      checked={enablePipelineStatusChecks}
                      onChange={setEnablePipelineStatusChecks}
                      style={{ marginLeft: 12, flexShrink: 0 }}
                    />
                  </div>
                  {enablePipelineStatusChecks && (
                    <div style={{ marginTop: 8 }}>
                      <div style={{ fontSize: '11px', color: '#666', marginBottom: 4 }}>
                        Monitor jobs start from ({circuitTimeZone || 'UTC'})
                      </div>
                      <DatePicker
                        showTime
                        value={pipelineMonitorStart ? dayjs(pipelineMonitorStart.format('YYYY-MM-DD HH:mm:ss')) : null}
                        onChange={(date) => setPipelineMonitorStart(date ? dayjs.tz(date.format('YYYY-MM-DD HH:mm:ss'), circuitTimeZone || 'UTC') : null)}
                        format="YYYY-MM-DD HH:mm"
                        placeholder="Day 3, 16:00 (default)"
                        style={{ width: '100%' }}
                        showNow={false}
                      />
                    </div>
                  )}
                  {jobCounts && enablePipelineStatusChecks && (
                    <div style={{ fontSize: '11px', color: '#1890ff', marginTop: 4 }}>
                      {jobCounts.stationsStatusCheck} stations + {jobCounts.radarStatusCheck} radar jobs will be created
                    </div>
                  )}
                </Card>
              </div>

              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                <Card size="small" style={{ flex: 1, padding: '12px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 500, marginBottom: 2, fontSize: '14px' }}>Archive Observations</div>
                      <div style={{ fontSize: '11px', color: '#666', lineHeight: '1.4' }}>
                        Archives station observations to GCS after the event ends. Scheduled for end of last day at 23:59:59.
                      </div>
                    </div>
                    <Switch
                      checked={enableArchiveObservations}
                      onChange={setEnableArchiveObservations}
                      style={{ marginLeft: 12, flexShrink: 0 }}
                    />
                  </div>
                  {jobCounts && enableArchiveObservations && (
                    <div style={{ fontSize: '11px', color: '#1890ff', marginTop: 4 }}>
                      {jobCounts.archiveObservations} job will be created
                    </div>
                  )}
                </Card>
              </div>

              {/* Summary */}
              {jobCounts && jobCounts.total > 0 && (
                <Card size="small" style={{ backgroundColor: '#f0f9ff', borderColor: '#1890ff', padding: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div>
                      <div style={{ fontWeight: 500, marginBottom: 2, fontSize: '14px' }}>Total Jobs to Create</div>
                      <div style={{ fontSize: '11px', color: '#666' }}>
                        All jobs will be created after confirmation. You can view and manage them in this page.
                      </div>
                    </div>
                    <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#1890ff', marginLeft: 16 }}>
                      {jobCounts.total}
                    </div>
                  </div>
                </Card>
              )}

              {jobCounts && jobCounts.total === 0 && (
                <Alert
                  title="No Jobs to Create"
                  description="No jobs will be created with the current settings. Please enable at least one job type."
                  type="warning"
                  showIcon
                />
              )}
            </Space>
          )}

          <div style={{ marginTop: 24, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setIsRecreateModalOpen(false)}>
                Cancel
              </Button>
              <Button
                type="primary"
                onClick={handleCreateJobs}
                loading={creatingJobs}
                disabled={!jobCounts || jobCounts.total === 0 || !selectedEvent?.sessions || selectedEvent.sessions.length === 0}
              >
                Create Jobs
              </Button>
            </Space>
          </div>
        </div>
      </Modal>

      {/* Job Logs Drawer */}
      <Drawer
        title={
          <Space>
            <FileTextOutlined />
            <span>Job Logs</span>
            {logDrawerJob && (
              <Tag color={JOB_STATUS_CONFIG[logDrawerJob.status]?.color}>
                {JOB_TYPE_CONFIG[logDrawerJob.job_type]?.label}
              </Tag>
            )}
          </Space>
        }
        placement="right"
        size={700}
        open={logDrawerOpen}
        onClose={() => setLogDrawerOpen(false)}
        extra={
          <Space size="small">
            {logEntries.length > 0 && (
              <>
                <Tooltip title="Copy to clipboard">
                  <Button
                    size="small"
                    icon={<CopyOutlined />}
                    onClick={() => {
                      const text = logEntries
                        .map(e => {
                          const ts = e.timestamp ? new Date(e.timestamp).toISOString() : '';
                          return `${ts}  ${e.severity.padEnd(7)}  ${e.message}`;
                        })
                        .join('\n');
                      navigator.clipboard.writeText(text);
                      message.success('Logs copied to clipboard');
                    }}
                  />
                </Tooltip>
                <Tooltip title="Save as file">
                  <Button
                    size="small"
                    icon={<DownloadOutlined />}
                    onClick={() => {
                      const text = logEntries
                        .map(e => {
                          const ts = e.timestamp ? new Date(e.timestamp).toISOString() : '';
                          return `${ts}  ${e.severity.padEnd(7)}  ${e.message}`;
                        })
                        .join('\n');
                      const blob = new Blob([text], { type: 'text/plain' });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      const jobType = logDrawerJob?.job_type || 'job';
                      const jobId = logDrawerJob?.id?.slice(0, 8) || '';
                      a.download = `${jobType}-${jobId}-logs.txt`;
                      a.click();
                      URL.revokeObjectURL(url);
                    }}
                  />
                </Tooltip>
              </>
            )}
            {logDrawerJob && logDrawerJob.status === 'running' && (
              <Button
                size="small"
                icon={<ReloadOutlined />}
                onClick={() => fetchJobLogs(logDrawerJob)}
                loading={logLoading}
              >
                Refresh
              </Button>
            )}
          </Space>
        }
      >
        {logLoading && (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Spin size="large" />
            <div style={{ marginTop: 12, color: 'rgba(0,0,0,0.45)' }}>Fetching logs from Cloud Logging...</div>
          </div>
        )}

        {logError && (
          <Alert
            type="error"
            message="Failed to load logs"
            description={logError}
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

        {!logLoading && !logError && logEntries.length === 0 && (
          <Empty description="No log entries found" />
        )}

        {!logLoading && logEntries.length > 0 && (
          <div
            style={{
              fontFamily: 'monospace',
              fontSize: '12px',
              lineHeight: '1.6',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-all',
              backgroundColor: theme === 'dark' ? '#1a1a1a' : '#f5f5f5',
              padding: '12px',
              borderRadius: '6px',
              maxHeight: 'calc(100vh - 160px)',
              overflow: 'auto',
            }}
          >
            {logEntries.map((entry, idx) => {
              const ts = entry.timestamp
                ? new Date(entry.timestamp).toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit', fractionalSecondDigits: 3 } as Intl.DateTimeFormatOptions)
                : '';
              const severityColor =
                entry.severity === 'ERROR' ? '#f5222d' :
                entry.severity === 'WARNING' ? '#faad14' :
                entry.severity === 'INFO' ? '#1890ff' :
                theme === 'dark' ? '#d9d9d9' : '#595959';

              return (
                <div key={idx} style={{ marginBottom: '2px' }}>
                  <span style={{ color: theme === 'dark' ? '#8c8c8c' : '#8c8c8c', marginRight: '8px' }}>{ts}</span>
                  <span style={{ color: severityColor }}>{entry.message}</span>
                </div>
              );
            })}
          </div>
        )}
      </Drawer>
    </div>
  );
};

export default JobsPage;

